import { ReactNode, RefObject } from "react";
import styled, { css } from "styled-components";
import { FloatingContext } from "@floating-ui/react";
import { ROW_GAP, SelectOption } from "../../Select.Parts";
import { InnerInput } from "../../search-input/SearchInput.Parts";
import { SelectionContainerProps, getRowHeight } from "../Trigger.Parts";

// TEXT

// STYLES
export const SelectionContainer = styled.div<ContainerProps>`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  align-content: flex-start;
  ${({ $rows }) => $rows > 1 && "flex-wrap: wrap;"}
  gap: ${ROW_GAP}rem;
  max-height: 0;
  opacity: 0;

  ${({ $active, $rows = 1, $small }) =>
    $active &&
    css`
      max-height: ${calculateContainerHeight($rows, $small)};
      opacity: 1;
    `}

  &:has(${InnerInput}:focus) {
    max-height: ${({ $rows = 1, $small }) =>
      calculateContainerHeight($rows, $small)};
    opacity: 1;
  }

  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
`;

// DATA

// TYPES
export type Props = {
  /** Static/Layout Props */
  name?: string; // The HTML name attribute for the hidden form inputs.
  rows: number; // The maximum number of rows for selection chips before overflow.
  label?: ReactNode; // The floating label content.
  placeholder?: ReactNode; // The placeholder text.
  search?: boolean; // Whether the search input is enabled.
  disabled?: boolean; // Whether the control is disabled.
  small?: boolean; // Whether the trigger uses the 40px small height.

  /** Visual State Props */
  hasContent: boolean; // True if selection, placeholder, or searchValue exists.

  /** Selection Data & Visibility */
  selection: (string | number)[]; // The current array of selected values.
  getLabelForValue: (value: string | number) => string; // Function to get the display label for a value.
  visibleSelections: (string | number)[]; // The subset of selections that are visible (not in overflow).
  overflowCount: number; // The count of hidden selections (+N).
  filteredOptions: SelectOption[]; // The list of options used for matching activeIndex.
  activeIndex: number | null; // The index of the item virtually focused in the dropdown.

  /** Handlers */
  setSelection: (newSelection: (string | number)[]) => void; // Function to remove/add selections.

  /** Refs & Floating UI Context */
  context: FloatingContext; // Floating UI context (contains .open, .dataRef).
  referenceId: string;
  inputRef: RefObject<HTMLInputElement | null>; // Ref to the search input for focusing.
  buttonRef: RefObject<HTMLDivElement | null>; // Ref to the base button element for width measurement.

  /** Children */
  searchInput: ReactNode; // The pre-rendered SelectInput component node.
  searchInputId: string; // The ID for the search input (for label association).
};

type ContainerProps = Pick<SelectionContainerProps, "$rows" | "$active"> & {
  $small?: boolean;
};

// CONSTANTS

// FUNCTIONS
const calculateContainerHeight = ($rows = 1, $small?: boolean): string =>
  `${$rows * getRowHeight($rows, $small) + ($rows - 1) * ROW_GAP}rem`;

// ASSETS
